<script setup lang="ts">
    import { useSqlEditorStore } from '@/stores/sqlEditorStore';
    import { isAdministrator } from '@/shared/directives/requirePermission';
    import { ref, watch } from 'vue';
    import { http } from '@/shared/httpWrapper';
    import { SavedQueryUsageUpdateRequest, updateSavedQueryUsage } from '@/shared/helpers/api';
    import useShow from '@/shared/composables/useShow';

    type Props = {
        show: boolean;
    };

    const props = defineProps<Props>();

    const emit = defineEmits(['close']);

    const store = useSqlEditorStore();
    const showModal = ref<boolean>(false);
    const usage = ref<string>('');
    const usedBy = ref<string>('');
    const usedByExternalApplication = ref<boolean>(false);
    const canUpdate = ref<boolean>(false);

    const { showError, showInfo } = useShow();

    watch(() => props.show, async () => {
        // Copy values from store for edit
        usage.value = store.loadedQueryUsage || '';
        usedBy.value = store.loadedQueryUsedBy || '';
        usedByExternalApplication.value = store.loadedQueryUsedByExternalApplication;

        showModal.value = props.show;

        canUpdate.value = store.loadedQueryOwnedByCurrentUser || (await isAdministrator());
    }, { immediate: true });

    async function onSave(): Promise<void> {
        try {
            if (!store.loadedQueryId) {
                throw Error('onSave() must not be called without a loaded query');
            }

            const changeRequest: SavedQueryUsageUpdateRequest = {
                usage: usage.value.trim(),
                usedBy: usedBy.value.trim(),
                usedByExternalApplication: usedByExternalApplication.value
            };

            await updateSavedQueryUsage(http, store.loadedQueryId, changeRequest);
            store.loadedQueryUpdateUsage(changeRequest);
            emit('close');
        } catch (err) {
            showError(err);
        }
    }

    async function onCopyGuid(): Promise<void> {
        if (!store.loadedQueryGuid) {
            showError('Guid not set');
        } else {
            await navigator.clipboard.writeText(store.loadedQueryGuid);
            showInfo('Query guid copied!');
        }
    }
</script>

<template>
    <b-modal
        v-model="showModal"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-label="Governance"
        close-button-aria-label="Close"
        aria-modal
        @close="emit('close')">
        <div
            class="modal-card"
            style="width: auto;">
            <header class="modal-card-head">
                <div class="modal-card-title">
                    <h2 class="title is-4">
                        Governance for this query
                    </h2>
                </div>
            </header>

            <section class="modal-card-body">
                <p class="block">
                    Query identified by: <code>{{ store.loadedQueryGuid }}</code>
                    <b-button
                        icon-left="content-copy"
                        size="is-small"
                        type="is-ghost"
                        title="Copy to clipboard"
                        @click="onCopyGuid" />
                </p>
                <p class="block">
                    Governance information may be updated by the query owner {{ store.loadedQueryUserName }}
                    or by any CommonLibrary administrator.
                </p>
                <b-field>
                    <b-checkbox
                        v-model="usedByExternalApplication"
                        :disabled="!canUpdate">
                        If checked, this indicates that some functionality somewhere depends on this query. Details must be provided below.
                    </b-checkbox>
                </b-field>
                <b-field label="Used by">
                    <b-input
                        v-if="canUpdate"
                        v-model="usedBy"
                        type="textarea"
                        maxlength="256"
                        placeholder="Description of caller(s) such as external API(s) or subsystem in CommonLibrary or AccessGroup etc. External API(s) should mention the API name here, comma separate them if more than one."
                        rows="2" />
                    <div
                        v-else
                        class="block">
                        {{ usedBy || 'No information provided' }}
                    </div>
                </b-field>
                <b-field label="Usage description">
                    <b-input
                        v-if="canUpdate"
                        v-model="usage"
                        type="textarea"
                        maxlength="1024"
                        placeholder="What is the query result used for"
                        rows="3" />
                    <div
                        v-else
                        class="block">
                        {{ usage || 'No information provided' }}
                    </div>
                </b-field>
            </section>

            <footer class="modal-card-foot is-justify-content-end">
                <b-field class="buttons">
                    <b-button
                        icon-left="close-circle"
                        @click="emit('close')">
                        Close
                    </b-button>
                    <b-button
                        v-if="canUpdate"
                        icon-left="floppy"
                        type="is-primary"
                        @click="onSave">
                        Update governance info
                    </b-button>
                </b-field>
            </footer>
        </div>
    </b-modal>
</template>
